export const CLASS_ACTIVITY_MAX_CLASS_LABEL_CHARS = 20;
export const CLASS_ACTIVITY_MAX_PHRASE_CHARS = 30;
export const CLASS_ACTIVITY_MAX_STORY_CHARS = 60;

export const CLASS_ACTIVITY_THEMES = [
  {
    id: 'interview_bingo',
    label: '친구 인터뷰 빙고',
    desc: '친구 찾아 사인 받기 4x4 빙고',
    default: true,
    exampleImage: '/images/events/class-activity-bingo.webp',
    exampleAlt: '3학년 2반 픽셀 게임 빙고 활동지 예시',
  },
  {
    id: 'balance_game',
    label: '우리 반 밸런스 게임',
    desc: 'A vs B 고르고 이유 쓰기',
    exampleImage: '/images/events/class-activity-balance.webp',
    exampleAlt: '1학년 3반 픽셀 게임 밸런스 활동지 예시',
  },
  {
    id: 'escape_room',
    label: '교실 방탈출',
    desc: '4단계 미션·암호 풀기',
  },
  {
    id: 'seasonal',
    label: '시즌 특집',
    desc: '추석·한글날 등 그때그때',
    exampleImage: '/images/events/class-activity-seasonal.webp',
    exampleAlt: '5학년 1반 픽셀 게임 시즌 활동지 예시',
  },
];

export const CLASS_ACTIVITY_EXAMPLES = CLASS_ACTIVITY_THEMES.filter((theme) => theme.exampleImage);

export const CLASS_ACTIVITY_CLASS_PRESETS = ['3학년 2반', '5학년 1반', '1학년 3반'];
export const CLASS_ACTIVITY_DEFAULT_CLASS_LABEL = CLASS_ACTIVITY_CLASS_PRESETS[0];

export const CLASS_ACTIVITY_SEASON_TOPICS = [
  { id: 'chuseok', label: '추석', activeUntil: '2026-09-26' },
  { id: 'hangeul', label: '한글날', activeUntil: '2026-10-09' },
  { id: 'exam_done', label: '시험 끝난 날', activeUntil: '2026-10-31' },
  { id: 'halloween', label: '가을·할로윈', activeUntil: '2026-11-30' },
];

export const CLASS_ACTIVITY_PIXEL_STYLE_DESCRIPTION = [
  '레트로 8비트 픽셀 게임 화면을 인쇄한 교실 미니게임 활동지. 젭퀴즈 TV 게임과 같은 톤.',
  '두꺼운 픽셀 테두리, 하트·코인·별·SCORE HUD, START 버튼 느낌, 또렷한 한글 픽셀 글씨.',
  '일반 학습지·시험지·교과서·파스텔 수채화·흰 배경 문제지는 절대 쓰지 마세요.',
].join(' ');

export const CLASS_ACTIVITY_LOCKED_STYLE = {
  id: 'pixel',
  label: '픽셀 게임',
  styleDescription: CLASS_ACTIVITY_PIXEL_STYLE_DESCRIPTION,
  default: true,
};

export const CLASS_ACTIVITY_STYLES = [CLASS_ACTIVITY_LOCKED_STYLE];

export const CLASS_ACTIVITY_GRADES = ['초1-2', '초3-4', '초5-6', '중1', '중2', '중3'];

export const CLASS_SIZE_NONE = '선택 안 함';

export const CLASS_SIZE_OPTIONS = [
  CLASS_SIZE_NONE,
  '~15명',
  '16~20명',
  '21~25명',
  '26~30명',
  '31명~',
];

export const CLASS_ACTIVITY_DEFAULT_GRADE = '초3-4';

const clampText = (value, maxChars) => (
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxChars)
);

export const normalizeClassActivityClassLabel = (value) => (
  clampText(value, CLASS_ACTIVITY_MAX_CLASS_LABEL_CHARS)
);

export const normalizeClassActivityPhrase = (value) => (
  clampText(value, CLASS_ACTIVITY_MAX_PHRASE_CHARS)
);

export const normalizeClassActivityStory = (value) => (
  clampText(value, CLASS_ACTIVITY_MAX_STORY_CHARS)
);

export const getDefaultClassActivityTheme = () => (
  CLASS_ACTIVITY_THEMES.find((theme) => theme.default) || CLASS_ACTIVITY_THEMES[0]
);

export const getDefaultClassActivityStyle = () => CLASS_ACTIVITY_LOCKED_STYLE;

export const getClassActivityTheme = (themeId) => (
  CLASS_ACTIVITY_THEMES.find((theme) => theme.id === themeId)
  || getDefaultClassActivityTheme()
);

export const getClassActivityStyle = () => CLASS_ACTIVITY_LOCKED_STYLE;

export const formatLocalDate = (now = new Date()) => {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getActiveSeasonTopic = (now = new Date()) => {
  const today = formatLocalDate(now);
  return CLASS_ACTIVITY_SEASON_TOPICS.find((topic) => topic.activeUntil >= today)
    || CLASS_ACTIVITY_SEASON_TOPICS[CLASS_ACTIVITY_SEASON_TOPICS.length - 1];
};

export const getClassActivitySeasonTopic = (topicId, now = new Date()) => (
  CLASS_ACTIVITY_SEASON_TOPICS.find((topic) => topic.id === topicId)
  || getActiveSeasonTopic(now)
);

const THEME_INSTRUCTIONS = {
  interview_bingo: [
    '- 활동 유형: 친구 인터뷰 빙고.',
    '- 4x4 빙고판 16칸을 만들고, 각 칸은 "방학에 여행 다녀온 친구", "나와 좋아하는 계절이 같은 친구"처럼 친구를 찾아 사인을 받는 미션으로 구성해주세요.',
    '- 칸에는 미션 문장과 사인 네모만 넣고, 친구 이름·학생 이름 기입란은 넣지 마세요.',
    '- 쉬운 문장으로 써주세요.',
  ].join('\n'),
  balance_game: [
    '- 활동 유형: 우리 반 밸런스 게임.',
    '- A vs B 선택 문항 10개를 음식·상상·학교생활이 골고루 섞이게 만들어주세요.',
    '- 각 문항 아래에 "내 선택과 이유"를 한 줄로 쓰는 칸을 넣어주세요.',
  ].join('\n'),
  escape_room: [
    '- 활동 유형: 교실 방탈출 미션지.',
    '- 4단계 미션을 간단한 암호 퍼즐, 협동 미션, 몸으로 하는 미션을 섞어서 만들어주세요.',
    '- 마지막에 최종 탈출 암호를 적는 칸을 넣어주세요.',
  ].join('\n'),
};

const SAFETY_LINES = [
  '- 학생 개인 실명·가명·이니셜은 넣지 마세요.',
  '- 학년·반·번호·이름 기입란([이름], [번호] 칸)은 만들지 마세요. 반 이름만 제목으로 넣으세요.',
  '- 점수·등수 요소는 넣지 마세요.',
  '- 교과서 지문을 그대로 옮기지 마세요.',
  '- 페이지 전체가 젭퀴즈식 픽셀 게임 UI여야 합니다. 하트·코인·별·두꺼운 픽셀 테두리·게임 HUD를 넣으세요.',
  '- 일반 학습지·시험지·점선 이름칸·교과서 레이아웃은 쓰지 마세요.',
  '- 비PRO 결과라면 하단에 작은 글씨로 whatquiz.co.kr을 넣어주세요.',
].join('\n');

export const buildClassActivityPrompt = ({
  classLabel,
  theme,
  seasonTopic,
  phrase,
  classStory,
  classSize,
} = {}) => {
  const selectedTheme = getClassActivityTheme(theme);
  const selectedSeason = getClassActivitySeasonTopic(seasonTopic);
  const safeClassLabel = normalizeClassActivityClassLabel(classLabel) || '우리 반';
  const safePhrase = normalizeClassActivityPhrase(phrase);
  const safeStory = normalizeClassActivityStory(classStory);
  const safeSize = CLASS_SIZE_OPTIONS.includes(classSize)
    && classSize !== CLASS_SIZE_NONE
    ? classSize
    : '';

  const themeBlock = selectedTheme.id === 'seasonal'
    ? [
      `- 활동 유형: ${selectedSeason.label} 특집 활동지.`,
      '- 논란 없는 확실한 상식 퀴즈 5개와 그리기/쓰기 활동 1개를 넣어주세요.',
    ].join('\n')
    : THEME_INSTRUCTIONS[selectedTheme.id];

  const lines = [
    `"${safeClassLabel}"를 위한 재미 활동지를 만들어주세요. 평가용이 아니라 학급 놀이·관계 형성용입니다.`,
    themeBlock,
    `- 표지와 헤더에 반 이름 "${safeClassLabel}"를 크게 넣어주세요.`,
  ];

  if (safePhrase) {
    lines.push(`- 응원 문구 "${safePhrase}"를 표지에 자연스럽게 넣어주세요.`);
  }
  if (safeStory) {
    lines.push(`- 우리 반 이야기를 여러 문항에 자연스럽게 변주해서 녹여주세요(최소 3곳): "${safeStory}"`);
  }
  if (safeSize) {
    lines.push(`- 우리 반 인원은 ${safeSize}입니다. 빙고 최종 미션이나 협동 미션에 인원수를 반영해주세요 (예: "우리 반 전원의 사인을 모아라").`);
  }
  lines.push(SAFETY_LINES);

  return lines.filter(Boolean).join('\n');
};
